import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowUpRight,
  Ban,
  Box,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  ExternalLink,
  Filter,
  Info,
  Lightbulb,
  LoaderCircle,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Save,
  ScanSearch,
  Search,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  Store,
  Truck,
  Trash2,
  XCircle,
} from "lucide-react";
import type { PageContext } from "../App";
import { api, getApiError } from "../api";
import {
  Button,
  Drawer,
  Field,
  IconButton,
  InlineError,
  Input,
  MarketplaceMark,
  MarketplaceSelector,
  Modal,
  PageHeader,
  Panel,
  Select,
  StatePanel,
  StatusPill,
  formatDate,
  formatMoney,
} from "../components/ui";
import { useAsync, useDebouncedValue } from "../hooks";
import { sellerCentralInventoryUrl } from "../sellerCentral";
import {
  MARKETPLACE_CODES,
  MARKETPLACE_META,
  type FulfillmentMode,
  type AppSettings,
  type ContentQualityIssue,
  type ListingMatrixRow,
  type MarketplaceListingIssue,
  type ListingPreview,
  type ListingStatus,
  type MarketplaceCode,
  type MarketplaceRepricingState,
  type SettingsTemplate,
  type ShippingOverviewItem,
  type ShippingTemplate,
} from "../types";
import { ProductTypeAttributesForm } from "../components/ProductTypeAttributesForm";
import { SkuIdentityFields } from "../components/SkuIdentityFields";
import type { ListingDraft, RolloutPreparation } from "../types";
import { IssueInbox } from "./MediaStudioPage";

type MatrixFilter = "all" | "de-active-foreign-missing" | "issues";

export function ListingsPage({ navigate, notify }: PageContext) {
  const [initialParams] = useState(() => new URLSearchParams(window.location.search));
  const [activeView, setActiveView] = useState<"catalog" | "issues" | "shipping">(() => {
    const view = initialParams.get("view");
    return view === "shipping" ? "shipping" : view === "issues" || initialParams.has("issue") ? "issues" : "catalog";
  });
  const [query, setQuery] = useState(initialParams.get("sku") ?? "");
  const debouncedQuery = useDebouncedValue(query, 350);
  const [filter, setFilter] = useState<MatrixFilter>("all");
  const [fulfillment, setFulfillment] = useState("");
  const [focusCountry, setFocusCountry] = useState<MarketplaceCode>("DE");
  const [selected, setSelected] = useState<ListingMatrixRow | null>(null);
  const [rolloutOpen, setRolloutOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [qualityScanning, setQualityScanning] = useState(false);
  const [qualityLoadingMore, setQualityLoadingMore] = useState(false);
  const [priceIssue, setPriceIssue] = useState<ContentQualityIssue | null>(null);

  const requestedMarketplace = initialParams.get("marketplace") as MarketplaceCode;
  const qualityParams = { seller_sku: initialParams.get("sku") || undefined, marketplace: MARKETPLACE_CODES.includes(requestedMarketplace) ? requestedMarketplace : undefined };
  const quality = useAsync(() => api.contentQualityIssues({ ...qualityParams, limit: 500 }), [], activeView === "issues");
  const qualityReloadRef = useRef(quality.reload);
  qualityReloadRef.current = quality.reload;

  const matrix = useAsync(
    () =>
      api.matrix({
        q: debouncedQuery || undefined,
        filter,
        fulfillment: fulfillment || undefined,
      }),
    [debouncedQuery, filter, fulfillment],
    activeView === "catalog",
  );

  const sync = async () => {
    setSyncing(true);
    try {
      const result = await api.sync();
      notify(`Vollständiger Abgleich gestartet · ${result.jobId}`, "success");
    } catch (error) {
      notify(getApiError(error), "error");
    } finally {
      setSyncing(false);
    }
  };

  const monitorQualityScan = async (jobId: string) => {
    for (let attempt = 0; attempt < 72; attempt += 1) {
      await delay(5_000);
      try {
        const job = await api.job(jobId);
        if (["succeeded", "failed", "cancelled"].includes(job.status)) {
          await qualityReloadRef.current();
          notify(
            job.status === "succeeded"
              ? job.message || "Amazon-Probleme wurden neu geprüft."
              : job.message || "Die Amazon-Prüfung wurde nicht erfolgreich beendet.",
            job.status === "succeeded" ? "success" : "error",
          );
          setQualityScanning(false);
          return;
        }
      } catch (error) {
        notify(getApiError(error), "error");
        setQualityScanning(false);
        return;
      }
    }
    setQualityScanning(false);
    notify("Die Prüfung läuft weiter. Den Status findest du unter Jobs & Audit.", "info");
  };

  const scanQuality = async () => {
    setQualityScanning(true);
    try {
      const result = await api.scanContentQuality({ limit: 500 });
      notify(
        result.reused
          ? "Die bereits laufende Amazon-Prüfung wird weiter beobachtet."
          : `Amazon-Probleme werden neu geprüft · Job ${result.jobId}`,
        "info",
      );
      void monitorQualityScan(result.jobId);
    } catch (error) {
      notify(getApiError(error), "error");
      setQualityScanning(false);
    }
  };

  const loadMoreQualityIssues = async () => {
    const cursor = quality.data?.nextCursor;
    if (!cursor || qualityLoadingMore) return;
    setQualityLoadingMore(true);
    try {
      const next = await api.contentQualityIssues({ ...qualityParams, cursor });
      quality.setData((current) =>
        current
          ? {
              ...next,
              items: [
                ...current.items,
                ...next.items.filter(
                  (item) => !current.items.some((existing) => existing.id === item.id),
                ),
              ],
              summary: current.summary,
            }
          : next,
      );
    } catch (error) {
      notify(getApiError(error), "error");
    } finally {
      setQualityLoadingMore(false);
    }
  };

  const handleQualityAction = async (issue: ContentQualityIssue) => {
    if (issue.fixAction.kind === "open_media_studio") {
      navigate("media", {
        tab: "studio",
        sku: issue.sellerSku,
        marketplace: issue.marketplaceCode,
        issue: issue.id,
      });
      return;
    }
    if (issue.fixAction.kind === "change_price") {
      setPriceIssue(issue);
      return;
    }
    if (issue.fixAction.kind === "refresh_listing") {
      try {
        const result = await api.verifyListing(issue.sellerSku);
        notify(
          result.jobId
            ? `Live-Prüfung gestartet · Job ${result.jobId}`
            : "Live-Prüfung wurde gestartet.",
          "success",
        );
      } catch (error) {
        notify(getApiError(error), "error");
      }
      return;
    }
    if (issue.fixAction.kind === "open_seller_central") {
      window.open(
        sellerCentralInventoryUrl(issue.marketplaceCode, issue),
        "_blank",
        "noopener,noreferrer",
      );
    }
  };

  const rows = matrix.data?.items ?? [];
  const issueCount = rows.filter((row) =>
    MARKETPLACE_CODES.some((code) =>
      ["inactive", "suppressed", "restricted"].includes(row.marketplaces[code]?.status ?? "") ||
      ["missing", "unknown"].includes(
        row.marketplaces[code]?.shippingConfigurationStatus ?? "",
      ),
    ),
  ).length;
  const missingCount = rows.filter(
    (row) =>
      row.marketplaces.DE?.status === "active" &&
      MARKETPLACE_CODES.some(
        (code) => code !== "DE" && row.marketplaces[code]?.status === "missing",
      ),
  ).length;

  const initialActionHandled = useRef(false);
  useEffect(() => {
    if (initialActionHandled.current) return;
    if (initialParams.get("rollout") === "1" && matrix.data) {
      const row = matrix.data.items.find(item => item.sellerSku === initialParams.get("sku"));
      if (row) { setSelected(row); setRolloutOpen(true); initialActionHandled.current = true; }
    }
    if (initialParams.get("action") === "price" && quality.data) {
      const issue = quality.data.items.find(item => item.id === initialParams.get("issue"));
      if (issue?.fixAction.kind === "change_price") { setPriceIssue(issue); initialActionHandled.current = true; }
    }
  }, [initialParams, matrix.data, quality.data]);

  return (
    <div className="listings-page">
      <PageHeader
        eyebrow="Artikelverwaltung"
        title="Artikel & Angebote"
        description="Angebote ansehen, Probleme bearbeiten oder Versandzuordnungen verwalten."
        actions={
          <>
            <Button
              variant="secondary"
              icon={<ScanSearch size={16} />}
              loading={qualityScanning}
              onClick={scanQuality}
            >
              Probleme neu prüfen
            </Button>
            <Button
              variant="secondary"
              icon={<RefreshCw size={16} />}
              loading={syncing}
              onClick={sync}
            >
              Amazon abgleichen
            </Button>
          </>
        }
      />

      <div className="work-tabs" role="group" aria-label="Artikelansicht">
        {([ ["catalog", "Angebote & Länder"], ["issues", "Probleme bearbeiten"], ["shipping", "Versand"] ] as const).map(([value, label]) => (
          <button type="button" key={value} aria-pressed={activeView === value} onClick={() => setActiveView(value)}>{label}</button>
        ))}
      </div>
      {activeView === "issues" && <>
      {initialParams.has("sku") && <div className="work-tools"><Button variant="ghost" onClick={() => navigate("listings", { view: "issues" })}>Alle Probleme anzeigen</Button><Button variant="ghost" onClick={() => navigate("home")}>Zur Aufgabenübersicht</Button></div>}
      <IssueInbox
        response={quality.data}
        loading={quality.loading}
        error={quality.error}
        loadingMore={qualityLoadingMore}
        initialQuery={new URLSearchParams(window.location.search).get("sku") ?? ""}
        initialSelectedId={initialParams.get("issue")}
        initialMarketplace={initialParams.get("marketplace")}
        focused={initialParams.has("sku")}
        onConfirmed={initialParams.get("from") === "tasks" ? async () => { navigate("home", { view: "waiting" }); } : undefined}
        onRetry={quality.reload}
        onLoadMore={loadMoreQualityIssues}
        onAction={handleQualityAction}
        notify={notify}
      />

      </>}
      {activeView === "shipping" && <>
        <ShippingCountryOverview notify={notify} />
        <ShippingSettingsManager notify={notify} />
      </>}
      {activeView === "catalog" && <>
      <section className="matrix-summary">
        <button
          className={`matrix-summary__card matrix-summary__card--warning ${filter === "de-active-foreign-missing" ? "is-active" : ""}`}
          onClick={() => setFilter("de-active-foreign-missing")}
        >
          <span className="matrix-summary__icon matrix-summary__icon--warning">
            <ArrowUpRight size={19} />
          </span>
          <span className="matrix-summary__copy">
            <small>Neue Länder anlegen</small>
            <strong>{missingCount}</strong>
            <span>Deutschland ist aktiv, im Ausland fehlt das Angebot</span>
          </span>
          <ChevronRight size={17} />
        </button>
        <button
          className={`matrix-summary__card matrix-summary__card--danger ${filter === "issues" ? "is-active" : ""}`}
          onClick={() => setFilter("issues")}
        >
          <span className="matrix-summary__icon matrix-summary__icon--danger">
            <CircleAlert size={19} />
          </span>
          <span className="matrix-summary__copy">
            <small>Aktion erforderlich</small>
            <strong>{issueCount}</strong>
            <span>Amazon-Fehler oder fehlende Versandzuordnung</span>
          </span>
          <ChevronRight size={17} />
        </button>
        <button
          className={`matrix-summary__card matrix-summary__card--info ${filter === "all" ? "is-active" : ""}`}
          onClick={() => setFilter("all")}
        >
          <span className="matrix-summary__icon"><Store size={19} /></span>
          <span className="matrix-summary__copy">
            <small>Gesamtübersicht</small>
            <strong>{matrix.data?.total ?? rows.length}</strong>
            <span>Alle Seller-SKUs und Länderzustände</span>
          </span>
          <ChevronRight size={17} />
        </button>
      </section>

      <Panel className="matrix-panel">
        <div className="matrix-toolbar">
          <div className="matrix-search">
            <Search size={18} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="SKU, ASIN oder Titel suchen …"
              aria-label="Listings durchsuchen"
            />
            {matrix.loading && <LoaderCircle size={16} className="spin" />}
          </div>
          <div className="matrix-filters">
            <Select
              aria-label="Fulfillment filtern"
              value={fulfillment}
              onChange={(event) => setFulfillment(event.target.value)}
            >
              <option value="">FBM & FBA</option>
              <option value="FBM">Nur FBM</option>
              <option value="FBA">Nur FBA</option>
            </Select>
            <Select
              className="marketplace-focus-select"
              aria-label="Marktplatzspalte hervorheben"
              value={focusCountry}
              onChange={(event) => setFocusCountry(event.target.value as MarketplaceCode)}
            >
              {MARKETPLACE_CODES.map((code) => (
                <option key={code} value={code}>
                  Fokus: {MARKETPLACE_META[code].label} ({code})
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="matrix-legend" aria-label="Bedeutung der Statusfarben">
          <strong>Statusfarben</strong>
          <span className="matrix-legend__item matrix-legend__item--active"><StatusPill status="active" /> kaufbar</span>
          <span className="matrix-legend__item matrix-legend__item--missing"><StatusPill status="missing" /> nicht angelegt</span>
          <span className="matrix-legend__item matrix-legend__item--inactive"><StatusPill status="inactive" /> nicht kaufbar</span>
          <span className="matrix-legend__item matrix-legend__item--suppressed"><StatusPill status="suppressed" /> Amazon-Fehler</span>
          <span className="matrix-legend__item matrix-legend__item--restricted"><StatusPill status="restricted" /> Verkauf gesperrt</span>
        </div>

        {matrix.data?.repricing?.status === "unavailable" && (
          <div className="repricing-feed-warning" role="status">
            <CircleAlert size={16} />
            <span>
              <strong>Amazon-Repricer-Status nicht verfügbar.</strong>{" "}
              {matrix.data.repricing.message ??
                "Preisänderungen bleiben bis zur nächsten erfolgreichen Abfrage gesperrt."}
            </span>
          </div>
        )}

        {matrix.error ? (
          <InlineError message={matrix.error} onRetry={() => void matrix.reload()} />
        ) : !matrix.loading && rows.length === 0 ? (
          <StatePanel
            type="empty"
            title={query ? "Keine passenden Listings" : "Noch keine Listings synchronisiert"}
            description={
              query
                ? "Passe Suche oder Filter an."
                : "Starte den ersten Amazon-Abgleich, um die Ländermatrix aufzubauen."
            }
            action={
              !query ? (
                <Button onClick={sync} loading={syncing} icon={<RefreshCw size={16} />}>
                  Jetzt synchronisieren
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="matrix-table-wrap">
            <table className="matrix-table">
              <thead>
                <tr>
                  <th className="matrix-table__product">
                    <span>Artikel</span>
                    <small>{matrix.loading ? "Wird geladen …" : `${matrix.data?.total ?? rows.length} SKUs`}</small>
                  </th>
                  {MARKETPLACE_CODES.map((code) => (
                    <th key={code} className={focusCountry === code ? "is-focused" : ""}>
                      <span className="matrix-country">
                        <MarketplaceMark code={code} />
                        <small>{MARKETPLACE_META[code].label}</small>
                      </span>
                    </th>
                  ))}
                  <th aria-label="Aktionen" />
                </tr>
              </thead>
              <tbody>
                {matrix.loading && !matrix.data
                  ? Array.from({ length: 5 }, (_, index) => <MatrixSkeleton key={index} />)
                  : rows.map((row) => (
                      <tr key={row.sellerSku} onClick={() => setSelected(row)}>
                        <td className="matrix-product-cell">
                          {row.imageUrl ? (
                            <img src={row.imageUrl} alt="" />
                          ) : (
                            <span className="matrix-product-cell__image"><Box size={22} /></span>
                          )}
                          <span className="matrix-product-cell__copy">
                            <strong>{row.title}</strong>
                            <small>{row.sellerSku} {row.asin ? `· ${row.asin}` : ""}</small>
                            <span className={`fulfillment-tag fulfillment-tag--${row.fulfillmentMode?.toLowerCase() ?? "fbm"}`}>
                              {row.fulfillmentMode ?? "FBM"}
                            </span>
                          </span>
                        </td>
                        {MARKETPLACE_CODES.map((code) => (
                          <td key={code} className={focusCountry === code ? "is-focused" : ""}>
                            <MarketplaceCell state={row.marketplaces[code]} />
                          </td>
                        ))}
                        <td>
                          <IconButton label={`${row.sellerSku} öffnen`}>
                            <MoreHorizontal size={18} />
                          </IconButton>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="matrix-panel__footer">
          <span>
            <Info size={14} />
            Status basiert auf dem letzten Live-Abgleich, nicht nur auf der Annahme einer API-Anfrage.
          </span>
          <span>Letzte Aktualisierung {formatDate(rows[0]?.updatedAt)}</span>
        </div>
      </Panel>

      </>}

      <Drawer
        open={Boolean(selected) && !rolloutOpen}
        title={selected?.title ?? ""}
        subtitle={`${selected?.sellerSku ?? ""}${selected?.asin ? ` · ASIN ${selected.asin}` : ""}`}
        onClose={() => setSelected(null)}
      >
        {selected && (
          <ListingDetail
            row={selected}
            notify={notify}
            openRollout={() => setRolloutOpen(true)}
            openQuality={() =>
              navigate("media", {
                tab: "actions",
                sku: selected.sellerSku,
              })
            }
            openMedia={() => navigate("media", { sku: selected.sellerSku })}
            openImageReview={() => navigate("image-reviews", { sku: selected.sellerSku, marketplace: focusCountry })}
            openSafetyReview={() => navigate("safety-reviews", { sku: selected.sellerSku, marketplace: focusCountry })}
          />
        )}
      </Drawer>

      {selected && (
        <RolloutModal
          open={rolloutOpen}
          row={selected}
          onClose={() => setRolloutOpen(false)}
          notify={notify}
        />
      )}
      {priceIssue && (
        <PriceCorrectionModal
          issue={priceIssue}
          notify={notify}
          onClose={() => setPriceIssue(null)}
          onSubmitted={() => void quality.reload()}
        />
      )}
    </div>
  );
}

function PriceCorrectionModal({
  issue,
  notify,
  onClose,
  onSubmitted,
}: {
  issue: ContentQualityIssue;
  notify: PageContext["notify"];
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [price, setPrice] = useState("");
  const [preview, setPreview] = useState<ListingPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const normalizedPrice = Number(price.replace(",", "."));
  const validPrice = Number.isFinite(normalizedPrice) && normalizedPrice > 0;
  const unchanged =
    validPrice &&
    typeof issue.currentPrice === "number" &&
    Math.abs(normalizedPrice - issue.currentPrice) < 0.005;
  const priceChangeAllowed = issue.repricing?.priceChangeAllowed === true;
  const previewMarket = preview?.marketplaces.find(
    (market) => market.code === issue.marketplaceCode,
  );
  const directUrl = sellerCentralInventoryUrl(issue.marketplaceCode, issue);

  const createPreview = async () => {
    if (!validPrice || !priceChangeAllowed) return;
    setPreviewing(true);
    setError(null);
    try {
      setPreview(
        await api.previewPriceChange({
          sellerSku: issue.sellerSku,
          marketplace: issue.marketplaceCode,
          price: normalizedPrice,
        }),
      );
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async () => {
    if (!preview) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.submitListing({
        previewId: preview.previewId,
        confirmationToken: preview.confirmationToken,
        idempotencyKey:
          globalThis.crypto?.randomUUID?.() ??
          `price-${Date.now()}-${issue.sellerSku}-${issue.marketplaceCode}`,
      });
      setDone(result.jobId);
      notify(
        `Preisänderung für ${issue.sellerSku} in ${issue.marketplaceCode} wurde eingereiht.`,
        "success",
      );
      onSubmitted();
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Amazon-Preisfehler korrigieren"
      description={`${issue.sellerSku} · ${MARKETPLACE_META[issue.marketplaceCode].label} (${issue.marketplaceCode})`}
      footer={
        done ? (
          <Button onClick={onClose}>Schließen</Button>
        ) : preview ? (
          <>
            <Button variant="ghost" onClick={() => setPreview(null)}>Zurück</Button>
            <Button
              icon={<Send size={16} />}
              disabled={!preview.canSubmit}
              loading={submitting}
              onClick={submit}
            >
              Bestätigten Preis an Amazon senden
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
            <Button
              icon={<ShieldCheck size={16} />}
              disabled={!priceChangeAllowed || !validPrice || unchanged}
              loading={previewing}
              onClick={createPreview}
            >
              Preisänderung prüfen
            </Button>
          </>
        )
      }
    >
      {error && <InlineError message={error} />}
      {done ? (
        <div className="modal-success">
          <span><Check size={28} /></span>
          <StatusPill status="queued" />
          <h3>Preisänderung wurde eingereiht</h3>
          <p>
            Job <code>{done}</code> sendet ausschließlich den bestätigten Preis und
            prüft das Angebot danach wieder live.
          </p>
        </div>
      ) : preview ? (
        <div className="price-correction-preview">
          <div className="price-correction-change">
            <div>
              <small>Bisheriger Amazon-Preis</small>
              <strong>{formatMoney(issue.currentPrice ?? undefined, issue.currency ?? "EUR")}</strong>
            </div>
            <ChevronRight size={19} />
            <div>
              <small>Bestätigter neuer Preis</small>
              <strong>{formatMoney(normalizedPrice, issue.currency ?? "EUR")}</strong>
            </div>
          </div>
          {(previewMarket?.diff ?? []).map((diff, index) => (
            <div className="shipping-assignment-diff" key={`${diff.path ?? diff.field}-${index}`}>
              <code>{diff.path ?? diff.field ?? "purchasable_offer"}</code>
              <span>{formatPreviewValue(diff.before, "nicht gesetzt")}</span>
              <ChevronRight size={14} />
              <strong>{formatPreviewValue(diff.after, "entfernt")}</strong>
            </div>
          ))}
          {previewMarket?.errors.map((message) => (
            <p className="preview-message preview-message--error" key={message}>{message}</p>
          ))}
          {previewMarket?.warnings.map((message) => (
            <p className="preview-message preview-message--warning" key={message}>{message}</p>
          ))}
          <div className="availability-rule">
            <ShieldCheck size={17} />
            <span>
              Diese Bestätigung enthält nur den Preis. Bestand, Versandvorlage,
              Produktdaten und Repricer-Regeln werden nicht übermittelt.
            </span>
          </div>
        </div>
      ) : (
        <div className="price-correction-form">
          <div className="price-correction-context">
            <div>
              <small>Was ist falsch?</small>
              <strong>{issue.message}</strong>
            </div>
            <div>
              <small>Warum?</small>
              <span>{issue.explanation}</span>
            </div>
          </div>
          <dl className="quality-facts">
            <div><dt>Land</dt><dd>{MARKETPLACE_META[issue.marketplaceCode].label} ({issue.marketplaceCode})</dd></div>
            <div><dt>Aktueller Preis</dt><dd>{formatMoney(issue.currentPrice ?? undefined, issue.currency ?? "EUR")}</dd></div>
            <div>
              <dt>Amazon-Repricer</dt>
              <dd>
                {issue.repricing?.status === "active"
                  ? "Aktiv"
                  : issue.repricing?.status === "inactive"
                    ? "Inaktiv"
                    : "Nicht sicher abrufbar"}
              </dd>
            </div>
          </dl>

          {!priceChangeAllowed && (
            <div className="repricing-feed-warning" role="status">
              <CircleAlert size={16} />
              <span>
                <strong>Direkte Preisänderung ist gesperrt.</strong>{" "}
                {issue.repricing?.status === "active"
                  ? "Der Repricer ist für diese SKU und dieses Land aktiv. Ändere den Preis dort oder prüfe das Angebot direkt in Seller Central."
                  : "Der Repricer-Status ist nicht sicher verfügbar. Der Manager arbeitet deshalb fail-closed."}
              </span>
            </div>
          )}

          <Field
            label="Neuer Verkaufspreis inklusive Umsatzsteuer"
            hint={
              unchanged
                ? "Der neue Preis entspricht dem letzten Amazon-Preis."
                : "Nach Klick auf „Preisänderung prüfen“ erscheint der exakte API-Diff."
            }
          >
            <Input
              type="number"
              min="0.01"
              step="0.01"
              inputMode="decimal"
              value={price}
              disabled={!priceChangeAllowed}
              placeholder="z. B. 28,89"
              onChange={(event) => {
                setPrice(event.target.value);
                setPreview(null);
              }}
            />
          </Field>

          <div className="availability-rule">
            <ShieldCheck size={17} />
            <span>Bei diesem Fix wird niemals Bestand mitgesendet.</span>
          </div>
          <Button
            variant="secondary"
            icon={<ExternalLink size={15} />}
            onClick={() => window.open(directUrl, "_blank", "noopener,noreferrer")}
          >
            Diesen Artikel in Seller Central öffnen
          </Button>
        </div>
      )}
    </Modal>
  );
}

function ShippingCountryOverview({ notify }: { notify: PageContext["notify"] }) {
  const [marketplace, setMarketplace] = useState<MarketplaceCode>("DE");
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 350);
  const [fulfillment, setFulfillment] = useState("");
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [editing, setEditing] = useState<ShippingOverviewItem | null>(null);
  const overview = useAsync(
    () =>
      api.shippingOverview({
        marketplace,
        q: debouncedQuery || undefined,
        fulfillment: fulfillment || undefined,
        problemsOnly,
        limit: 500,
      }),
    [marketplace, debouncedQuery, fulfillment, problemsOnly],
  );
  const selectedSummary = overview.data?.summary[marketplace];
  const totalProblems = MARKETPLACE_CODES.reduce((total, code) => {
    const country = overview.data?.summary[code];
    return total + (country?.missingFbmTemplate ?? 0) + (country?.unknown ?? 0);
  }, 0);

  return (
    <Panel className="shipping-overview-panel">
      <header className="shipping-overview-header">
        <div className="shipping-overview-title">
          <span><Truck size={20} /></span>
          <div>
            <strong>Versandarten nach Ländern</strong>
            <p>
              Jeder vorhandene Artikel mit FBM/FBA und der aktuell bei Amazon
              hinterlegten Versandvorlage. Die Zuordnung wird beim Amazon-Abgleich mit aktualisiert.
            </p>
          </div>
        </div>
        <div className="shipping-overview-header__actions">
          <span className={totalProblems ? "shipping-problem-count has-problems" : "shipping-problem-count is-complete"}>
            {totalProblems ? <CircleAlert size={15} /> : <CheckCircle2 size={15} />}
            {totalProblems ? `${totalProblems} Zuordnungen prüfen` : "Alle Zuordnungen vollständig"}
          </span>
          <IconButton label="Versandübersicht neu laden" onClick={() => void overview.reload()}>
            <RefreshCw size={17} className={overview.loading ? "spin" : ""} />
          </IconButton>
        </div>
      </header>

      <div className="shipping-country-selector" aria-label="Land für Versandübersicht wählen">
        {MARKETPLACE_CODES.map((code) => {
          const country = overview.data?.summary[code];
          const problems = (country?.missingFbmTemplate ?? 0) + (country?.unknown ?? 0);
          return (
            <button
              type="button"
              key={code}
              className={`shipping-country-card ${marketplace === code ? "is-active" : ""} ${problems ? "has-problems" : ""}`}
              onClick={() => setMarketplace(code)}
            >
              <MarketplaceMark code={code} decorative />
              <span>
                <strong>{MARKETPLACE_META[code].label}</strong>
                <small>{country?.total ?? 0} Artikel · {country?.fbm ?? 0} FBM · {country?.fba ?? 0} FBA</small>
              </span>
              {problems ? (
                <em><AlertTriangle size={13} /> {problems}</em>
              ) : (
                <CheckCircle2 size={15} className="shipping-country-card__ok" />
              )}
            </button>
          );
        })}
      </div>

      <div className="shipping-overview-toolbar">
        <div className="matrix-search">
          <Search size={18} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Artikel in ${MARKETPLACE_META[marketplace].label} suchen …`}
            aria-label="Versandzuordnungen durchsuchen"
          />
          {overview.loading && <LoaderCircle size={16} className="spin" />}
        </div>
        <div className="shipping-overview-filters">
          <Select
            value={fulfillment}
            aria-label="Versandart filtern"
            onChange={(event) => setFulfillment(event.target.value)}
          >
            <option value="">FBM & FBA</option>
            <option value="FBM">Nur FBM</option>
            <option value="FBA">Nur FBA</option>
          </Select>
          <button
            type="button"
            className={`shipping-problem-toggle ${problemsOnly ? "is-active" : ""}`}
            onClick={() => setProblemsOnly((current) => !current)}
            aria-pressed={problemsOnly}
          >
            <AlertTriangle size={15} /> Nur fehlende Zuordnungen
          </button>
        </div>
      </div>

      {overview.error ? (
        <InlineError message={overview.error} onRetry={() => void overview.reload()} />
      ) : !overview.loading && (overview.data?.items.length ?? 0) === 0 ? (
        <StatePanel
          type="empty"
          title={problemsOnly ? "Keine offenen Versandfehler" : "Keine Artikel für diese Auswahl"}
          description={
            problemsOnly
              ? `In ${MARKETPLACE_META[marketplace].label} besitzen alle vorhandenen FBM-Angebote eine Versandvorlage.`
              : "Passe Suche oder Filter an oder starte einen Amazon-Abgleich."
          }
        />
      ) : (
        <div className="shipping-overview-table-wrap">
          <table className="shipping-overview-table">
            <thead>
              <tr>
                <th>Artikel</th>
                <th>Amazon-Status</th>
                <th>Versandweg</th>
                <th>Amazon-Versandvorlage</th>
                <th>Zuordnung</th>
                <th>Live geprüft</th>
                <th aria-label="Versandvorlage ändern" />
              </tr>
            </thead>
            <tbody>
              {(overview.data?.items ?? []).map((item) => (
                <ShippingOverviewRow
                  item={item}
                  key={`${item.marketplace}-${item.sellerSku}`}
                  onEdit={() => setEditing(item)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <footer className="shipping-overview-footer">
        <span>
          <Info size={14} />
          {selectedSummary?.missingFbmTemplate ?? 0} FBM-Angebote ohne erkannte Versandvorlage
        </span>
        <span>
          {overview.data && overview.data.total > overview.data.items.length
            ? `${overview.data.items.length} von ${overview.data.total} angezeigt`
            : `${overview.data?.total ?? 0} Artikel`}
          {overview.data?.refreshedAt ? ` · Stand ${formatDate(overview.data.refreshedAt)}` : ""}
        </span>
      </footer>
      {editing && (
        <ShippingAssignmentModal
          item={editing}
          notify={notify}
          onClose={() => setEditing(null)}
          onSubmitted={() => void overview.reload()}
        />
      )}
    </Panel>
  );
}

function ShippingOverviewRow({
  item,
  onEdit,
}: {
  item: ShippingOverviewItem;
  onEdit: () => void;
}) {
  const shippingStatus = item.shippingConfigurationStatus;
  const configured = shippingStatus === "configured" || shippingStatus === "amazon_fulfilled";
  const fulfillment = item.fulfillmentMode ?? "–";
  const templateName =
    shippingStatus === "amazon_fulfilled"
      ? "Versand durch Amazon"
      : item.shippingTemplate || item.shippingTemplateId;
  return (
    <tr className={configured ? "" : "has-shipping-problem"}>
      <td>
        <div className="shipping-product">
          {item.imageUrl ? (
            <img src={item.imageUrl} alt="" />
          ) : (
            <span><Box size={19} /></span>
          )}
          <div>
            <strong>{item.title || item.sellerSku}</strong>
            <small>{item.sellerSku}{item.asin ? ` · ${item.asin}` : ""}</small>
          </div>
        </div>
      </td>
      <td><StatusPill status={item.status} /></td>
      <td>
        <span className={`shipping-fulfillment shipping-fulfillment--${String(fulfillment).toLowerCase()}`}>
          {fulfillment === "FBA" ? <Store size={14} /> : <Truck size={14} />}
          {fulfillment}
        </span>
      </td>
      <td>
        {templateName ? (
          <div className="shipping-template-name">
            <strong>{templateName}</strong>
            {item.shippingTemplateId && item.shippingTemplateId !== templateName && (
              <small>ID {item.shippingTemplateId}</small>
            )}
          </div>
        ) : (
          <span className="shipping-template-missing">Nicht erkannt</span>
        )}
      </td>
      <td>
        <span className={`shipping-assignment-status shipping-assignment-status--${shippingStatus}`}>
          {configured ? <CheckCircle2 size={14} /> : <CircleAlert size={14} />}
          {shippingStatus === "configured"
            ? "Hinterlegt"
            : shippingStatus === "amazon_fulfilled"
              ? "Amazon FBA"
              : shippingStatus === "missing"
                ? "Vorlage fehlt"
                : "Unbekannt"}
        </span>
      </td>
      <td><span className="shipping-verified-at">{formatDate(item.lastVerifiedAt ?? item.updatedAt)}</span></td>
      <td>
        <Button
          variant="ghost"
          className="shipping-edit-button"
          disabled={item.fulfillmentMode !== "FBM"}
          title={item.fulfillmentMode === "FBM" ? "Amazon-Versandvorlage ändern" : "FBA wird durch Amazon versendet"}
          onClick={onEdit}
        >
          Ändern
        </Button>
      </td>
    </tr>
  );
}

function ShippingAssignmentModal({
  item,
  notify,
  onClose,
  onSubmitted,
}: {
  item: ShippingOverviewItem;
  notify: PageContext["notify"];
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const templates = useAsync(
    () => api.shippingTemplates(item.marketplace),
    [item.marketplace],
  );
  const [templateId, setTemplateId] = useState(item.shippingTemplateId ?? "");
  const [preview, setPreview] = useState<ListingPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    if (templateId || !templates.data?.length) return;
    const current = templates.data.find(
      (template) =>
        template.name === item.shippingTemplate ||
        template.id === item.shippingTemplate ||
        template.templateId === item.shippingTemplate,
    );
    if (current) setTemplateId(current.templateId ?? current.id ?? current.name);
  }, [templates.data, templateId, item.shippingTemplate]);

  const createPreview = async () => {
    if (!templateId) return;
    setPreviewing(true);
    setError(null);
    try {
      setPreview(
        await api.previewShippingAssignment({
          sellerSku: item.sellerSku,
          marketplace: item.marketplace,
          shippingTemplateId: templateId,
        }),
      );
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async () => {
    if (!preview) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.submitListing({
        previewId: preview.previewId,
        confirmationToken: preview.confirmationToken,
        idempotencyKey:
          globalThis.crypto?.randomUUID?.() ?? `shipping-${Date.now()}-${item.sellerSku}`,
      });
      setDone(result.jobId);
      notify(`Versandvorlage für ${item.sellerSku} wurde zur Übermittlung eingereiht.`, "success");
      onSubmitted();
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setSubmitting(false);
    }
  };

  const currentTemplate = item.shippingTemplate || item.shippingTemplateId || "Nicht erkannt";
  const selectedTemplate = templates.data?.find(
    (template) => (template.templateId ?? template.id ?? template.name) === templateId,
  );
  const previewMarket = preview?.marketplaces.find(
    (market) => market.code === item.marketplace,
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Amazon-Versandvorlage ändern"
      description={`${item.sellerSku} · ${MARKETPLACE_META[item.marketplace].label}`}
      footer={
        done ? (
          <Button onClick={onClose}>Schließen</Button>
        ) : preview ? (
          <>
            <Button variant="ghost" onClick={() => setPreview(null)}>Zurück</Button>
            <Button
              icon={<Send size={16} />}
              disabled={!preview.canSubmit}
              loading={submitting}
              onClick={submit}
            >
              Änderung an Amazon senden
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
            <Button
              icon={<ShieldCheck size={16} />}
              disabled={!templateId || templateId === item.shippingTemplateId}
              loading={previewing}
              onClick={createPreview}
            >
              Änderung prüfen
            </Button>
          </>
        )
      }
    >
      {error && <InlineError message={error} />}
      {done ? (
        <div className="modal-success">
          <span><Check size={28} /></span>
          <StatusPill status="queued" />
          <h3>Versandänderung wurde eingereiht</h3>
          <p>Job <code>{done}</code> sendet nur die bestätigte Versandvorlage und prüft das Listing danach live.</p>
        </div>
      ) : preview ? (
        <div className="shipping-assignment-preview">
          <div className="shipping-assignment-change">
            <div><small>Bisher</small><strong>{currentTemplate}</strong></div>
            <ChevronRight size={18} />
            <div><small>Neu</small><strong>{selectedTemplate?.name ?? templateId}</strong></div>
          </div>
          {(previewMarket?.diff ?? []).map((diff, index) => (
            <div className="shipping-assignment-diff" key={index}>
              <code>{diff.path ?? diff.field ?? "merchant_shipping_group"}</code>
              <span>{formatPreviewValue(diff.before, "nicht gesetzt")}</span>
              <ChevronRight size={14} />
              <strong>{formatPreviewValue(diff.after, "entfernt")}</strong>
            </div>
          ))}
          {previewMarket?.errors.map((message) => (
            <p className="preview-message preview-message--error" key={message}>{message}</p>
          ))}
          {previewMarket?.warnings.map((message) => (
            <p className="preview-message preview-message--warning" key={message}>{message}</p>
          ))}
          <div className="availability-rule">
            <ShieldCheck size={17} />
            <span>Preis und Bestand werden bei dieser Änderung nicht übermittelt.</span>
          </div>
        </div>
      ) : (
        <div className="shipping-assignment-form">
          <div className="shipping-assignment-change">
            <div><small>Aktuell bei Amazon</small><strong>{currentTemplate}</strong></div>
            <ChevronRight size={18} />
            <div><small>Neue Zuordnung</small><strong>{selectedTemplate?.name ?? "Bitte wählen"}</strong></div>
          </div>
          <Field
            label={`Amazon-Versandvorlage für ${MARKETPLACE_META[item.marketplace].label}`}
            hint="Es werden ausschließlich Vorlagen angezeigt, die Amazon für dieses Verkäuferkonto und Land liefert."
          >
            <Select
              value={templateId}
              disabled={templates.loading}
              onChange={(event) => {
                setTemplateId(event.target.value);
                setPreview(null);
              }}
            >
              <option value="">{templates.loading ? "Amazon-Vorlagen werden geladen …" : "Bitte wählen …"}</option>
              {(templates.data ?? []).map((template) => {
                const value = template.templateId ?? template.id ?? template.name;
                return <option key={value} value={value}>{template.name}</option>;
              })}
            </Select>
          </Field>
          {templates.error && <InlineError message={templates.error} onRetry={() => void templates.reload()} />}
        </div>
      )}
    </Modal>
  );
}

function shippingPresetDefaults(template: SettingsTemplate) {
  const raw = template.values.defaultTemplates;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {} as Partial<Record<MarketplaceCode, string>>;
  }
  const result: Partial<Record<MarketplaceCode, string>> = {};
  MARKETPLACE_CODES.forEach((code) => {
    const value = (raw as Record<string, unknown>)[code];
    if (typeof value === "string" && value.trim()) result[code] = value.trim();
  });
  return result;
}

function ShippingSettingsManager({ notify }: { notify: PageContext["notify"] }) {
  const settings = useAsync(api.shippingSettings, []);
  const allTemplates = useAsync(api.templates, []);
  const [marketplace, setMarketplace] = useState<MarketplaceCode>("DE");
  const amazonTemplates = useAsync(
    () => api.shippingTemplates(marketplace),
    [marketplace],
  );
  const [defaults, setDefaults] = useState<Partial<Record<MarketplaceCode, string>>>({});
  const [presetName, setPresetName] = useState("");
  const [saving, setSaving] = useState(false);
  const [presetSaving, setPresetSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) setDefaults({ ...settings.data.defaultTemplates });
  }, [settings.data]);

  const presets = (allTemplates.data ?? []).filter(
    (template) => template.kind === "shipping_assignment",
  );
  const currentDefault = defaults[marketplace] ?? "";

  const saveDefaults = async (nextDefaults = defaults) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updateShippingSettings(nextDefaults);
      settings.setData(updated);
      setDefaults({ ...updated.defaultTemplates });
      notify("Standard-Versandvorlagen für die Länder wurden gespeichert.", "success");
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setSaving(false);
    }
  };

  const createPreset = async () => {
    if (!presetName.trim() || Object.keys(defaults).length === 0) return;
    setPresetSaving(true);
    setError(null);
    try {
      await api.createTemplate({
        name: presetName.trim(),
        kind: "shipping_assignment",
        marketplace: null,
        values: { defaultTemplates: defaults },
      });
      setPresetName("");
      await allTemplates.reload();
      notify("Neue Playzeug-Zuordnungsvorlage wurde angelegt.", "success");
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setPresetSaving(false);
    }
  };

  const applyPreset = async (template: SettingsTemplate) => {
    const values = shippingPresetDefaults(template);
    setDefaults(values);
    await saveDefaults(values);
  };

  const deletePreset = async (template: SettingsTemplate) => {
    if (!window.confirm(`Zuordnungsvorlage „${template.name}“ wirklich löschen?`)) return;
    setError(null);
    try {
      await api.deleteTemplate(template.id);
      await allTemplates.reload();
      notify("Zuordnungsvorlage wurde gelöscht.", "success");
    } catch (reason) {
      setError(getApiError(reason));
    }
  };

  const sellerCentralUrl =
    settings.data?.amazonTemplateCreation.sellerCentralUrl ??
    "https://sellercentral.amazon.de/sbr";

  return (
    <Panel className="shipping-settings-panel">
      <header className="shipping-settings-header">
        <div>
          <span><SlidersHorizontal size={20} /></span>
          <div>
            <strong>Versandeinstellungen & Vorlagen</strong>
            <p>Lege je Land eine Standardvorlage für neue Angebote fest und speichere wiederverwendbare Zuordnungsvorlagen.</p>
          </div>
        </div>
        <Button
          variant="secondary"
          icon={<ExternalLink size={16} />}
          onClick={() => window.open(sellerCentralUrl, "_blank", "noopener,noreferrer")}
        >
          Neue Amazon-Vorlage in Seller Central
        </Button>
      </header>
      {error && <InlineError message={error} />}
      <div className="shipping-settings-api-note">
        <Info size={16} />
        <span>
          Amazon erlaubt per SP-API das Abrufen und Zuweisen vorhandener Vorlagen. Versandzonen,
          Laufzeiten und Preise einer komplett neuen Amazon-Vorlage werden in Seller Central angelegt.
        </span>
      </div>
      <div className="shipping-settings-layout">
        <section className="shipping-defaults-section">
          <div className="shipping-settings-section-heading">
            <div><strong>Ländervorgaben</strong><small>Automatische Vorauswahl beim Anlegen neuer Auslandsangebote</small></div>
            <Button variant="secondary" icon={<Save size={15} />} loading={saving} onClick={() => void saveDefaults()}>
              Vorgaben speichern
            </Button>
          </div>
          <div className="shipping-default-country-list">
            {MARKETPLACE_CODES.map((code) => (
              <button
                type="button"
                key={code}
                className={marketplace === code ? "is-active" : ""}
                onClick={() => setMarketplace(code)}
              >
                <MarketplaceMark code={code} decorative />
                <span><strong>{MARKETPLACE_META[code].label}</strong><small>{defaults[code] ? "Standard gesetzt" : "Noch kein Standard"}</small></span>
                {defaults[code] ? <CheckCircle2 size={15} /> : <CircleAlert size={15} />}
              </button>
            ))}
          </div>
          <Field
            label={`Standard-Versandvorlage für ${MARKETPLACE_META[marketplace].label}`}
            hint="Diese Vorgabe wird beim Länder-Rollout vorausgewählt; gesendet wird sie erst nach deiner Vorschau und Bestätigung."
          >
            <div className="shipping-default-select-row">
              <Select
                value={currentDefault}
                disabled={amazonTemplates.loading}
                onChange={(event) => {
                  const value = event.target.value;
                  setDefaults((current) => {
                    const next = { ...current };
                    if (value) next[marketplace] = value;
                    else delete next[marketplace];
                    return next;
                  });
                }}
              >
                <option value="">{amazonTemplates.loading ? "Amazon-Vorlagen werden geladen …" : "Kein Standard"}</option>
                {(amazonTemplates.data ?? []).map((template) => {
                  const value = template.templateId ?? template.id ?? template.name;
                  return <option key={value} value={value}>{template.name}</option>;
                })}
              </Select>
              <IconButton label="Amazon-Vorlagen neu laden" onClick={() => void amazonTemplates.reload()}>
                <RefreshCw size={17} className={amazonTemplates.loading ? "spin" : ""} />
              </IconButton>
            </div>
          </Field>
          {amazonTemplates.error && <InlineError message={amazonTemplates.error} onRetry={() => void amazonTemplates.reload()} />}
        </section>

        <section className="shipping-presets-section">
          <div className="shipping-settings-section-heading">
            <div><strong>Playzeug-Zuordnungsvorlagen</strong><small>Komplette Länderbelegung wiederverwenden</small></div>
          </div>
          <div className="shipping-preset-create">
            <Input
              value={presetName}
              placeholder="Name der neuen Vorlage …"
              onChange={(event) => setPresetName(event.target.value)}
            />
            <Button
              variant="secondary"
              icon={<Plus size={15} />}
              loading={presetSaving}
              disabled={!presetName.trim() || Object.keys(defaults).length === 0}
              onClick={() => void createPreset()}
            >
              Aktuelle Länderbelegung als Vorlage speichern
            </Button>
          </div>
          {allTemplates.error ? (
            <InlineError message={allTemplates.error} onRetry={() => void allTemplates.reload()} />
          ) : presets.length === 0 ? (
            <div className="shipping-presets-empty">Noch keine eigene Zuordnungsvorlage angelegt.</div>
          ) : (
            <div className="shipping-preset-list">
              {presets.map((template) => {
                const values = shippingPresetDefaults(template);
                return (
                  <article key={template.id}>
                    <div>
                      <strong>{template.name}</strong>
                      <span>
                        {MARKETPLACE_CODES.filter((code) => values[code]).map((code) => (
                          <small key={code}>{code}</small>
                        ))}
                      </span>
                    </div>
                    <Button variant="ghost" onClick={() => void applyPreset(template)}>Übernehmen</Button>
                    <IconButton label={`${template.name} löschen`} onClick={() => void deletePreset(template)}>
                      <Trash2 size={16} />
                    </IconButton>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </Panel>
  );
}

function MarketplaceCell({
  state,
}: {
  state: ListingMatrixRow["marketplaces"][MarketplaceCode] | undefined;
}) {
  const status: ListingStatus = state?.status ?? "missing";
  return (
    <div className={`marketplace-cell marketplace-cell--${status}`}>
      <StatusPill status={status} />
      {typeof state?.price === "number" ? (
        <strong>{formatMoney(state.price, state.currency ?? "EUR")}</strong>
      ) : (
        <span className="marketplace-cell__dash">–</span>
      )}
      {state?.issues?.length ? (
        <small className="marketplace-cell__issue">
          <AlertTriangle size={11} /> {state.issues.length}
        </small>
      ) : ["missing", "unknown"].includes(state?.shippingConfigurationStatus ?? "") ? (
        <small className="marketplace-cell__issue">
          <Truck size={11} /> Versand
        </small>
      ) : state?.lastVerifiedAt ? (
        <small>{formatDate(state.lastVerifiedAt)}</small>
      ) : null}
      <RepricingBadge repricing={state?.repricing} compact />
    </div>
  );
}

function RepricingBadge({
  repricing,
  compact = false,
  label: customLabel,
}: {
  repricing: MarketplaceRepricingState | undefined;
  compact?: boolean;
  label?: string;
}) {
  if (!repricing) return null;
  const active = repricing.status === "active";
  const inactive = repricing.status === "inactive";
  const Icon = active ? ShieldCheck : inactive ? Check : CircleAlert;
  const label = active
    ? "Repricer aktiv"
    : inactive
      ? "Repricer nicht aktiv"
      : "Repricer unbekannt";
  const displayLabel = customLabel ?? label;
  const description = active
    ? "Preisänderungen in diesem Land sind im Amazon Manager gesperrt."
    : inactive
      ? "Preisänderungen in diesem Land dürfen vom Amazon Manager übermittelt werden."
      : "Repricer-Status nicht abrufbar. Preisänderungen bleiben vorsichtshalber gesperrt.";
  return (
    <span
      className={"repricing-badge repricing-badge--" + repricing.status}
      title={description}
    >
      <Icon size={compact ? 12 : 14} />
      {displayLabel}
    </span>
  );
}
function MatrixSkeleton() {
  return (
    <tr className="matrix-skeleton">
      <td><span /><div><i /><i /></div></td>
      {MARKETPLACE_CODES.map((code) => <td key={code}><i /></td>)}
      <td><i /></td>
    </tr>
  );
}

function ListingDetail({
  row,
  notify,
  openRollout,
  openQuality,
  openMedia,
  openImageReview,
  openSafetyReview,
}: {
  row: ListingMatrixRow;
  notify: PageContext["notify"];
  openRollout: () => void;
  openQuality: () => void;
  openMedia: () => void;
  openImageReview: () => void;
  openSafetyReview: () => void;
}) {
  const [verifying, setVerifying] = useState(false);
  const missingCountries = MARKETPLACE_CODES.filter(
    (code) => row.marketplaces[code]?.status === "missing",
  );
  const suppressedCountries = MARKETPLACE_CODES.filter(
    (code) => row.marketplaces[code]?.status === "suppressed",
  );

  const verify = async () => {
    setVerifying(true);
    try {
      const result = await api.verifyListing(row.sellerSku);
      notify(
        result.jobId
          ? `Live-Prüfung gestartet · ${result.jobId}`
          : "Live-Prüfung wurde gestartet.",
        "success",
      );
    } catch (error) {
      notify(getApiError(error), "error");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="listing-detail">
      <div className="listing-detail__actions">
        <Button
          disabled={missingCountries.length === 0}
          onClick={openRollout}
          icon={<ArrowUpRight size={17} />}
        >
          Fehlende Länder anlegen
        </Button>
        <Button
          variant="secondary"
          loading={verifying}
          onClick={verify}
          icon={<RefreshCw size={16} />}
        >
          Live prüfen
        </Button>
      </div>

      {suppressedCountries.length > 0 && (
        <section className="listing-suppression-overview">
          <header>
            <span><Ban size={18} /></span>
            <div>
              <strong>
                {suppressedCountries.length === 1
                  ? "Ein Marketplace ist unterdrückt"
                  : `${suppressedCountries.length} Marketplaces sind unterdrückt`}
              </strong>
              <p>
                Hier siehst du nur bestätigte Amazon-Befunde. Fehlt ein Grund, wird
                keine Ursache geraten.
              </p>
            </div>
          </header>
          <div>
            {suppressedCountries.map((code) => {
              const state = row.marketplaces[code];
              const issue = state?.issues?.find((entry) =>
                (entry.enforcementActions ?? []).some((action) =>
                  action.includes("SUPPRESSED"),
                ),
              ) ?? state?.issues?.[0];
              return (
                <article key={code}>
                  <MarketplaceMark code={code} decorative />
                  <div>
                    <strong>{MARKETPLACE_META[code].label}</strong>
                    <p>
                      {issue?.message ??
                        "Amazon bestätigt die Unterdrückung, hat aber noch keinen strukturierten Grund geliefert."}
                    </p>
                    <small>
                      {issue?.code
                        ? `Amazon-Code ${issue.code}`
                        : "Grund nach gezielter Qualitätsprüfung erneut abrufen"}
                    </small>
                  </div>
                </article>
              );
            })}
          </div>
          <Button
            variant="secondary"
            onClick={openQuality}
            icon={<CircleAlert size={16} />}
          >
            Vollständige Diagnose öffnen
          </Button>
        </section>
      )}

      <section>
        <div className="drawer-section-heading">
          <h3>Länderstatus</h3>
          <span>{missingCountries.length} fehlend</span>
        </div>
        <div className="detail-country-list">
          {MARKETPLACE_CODES.map((code) => {
            const state = row.marketplaces[code];
            const status = state?.status ?? "missing";
            return (
              <article key={code} className={`detail-country detail-country--${status}`}>
                <header>
                  <MarketplaceMark code={code} decorative />
                  <div>
                    <strong>{MARKETPLACE_META[code].label}</strong>
                    <small>{MARKETPLACE_META[code].vat}% MwSt.</small>
                  </div>
                  <StatusPill status={status} />
                  <RepricingBadge
                    repricing={state?.repricing}
                    label={state?.repricing?.status === "active"
                      ? "Aktiv gereprict · Preis gesperrt"
                      : state?.repricing?.status === "inactive"
                        ? "Nicht aktiv gereprict · Preis änderbar"
                        : "Status unbekannt · Preis gesperrt"}
                  />
                </header>
                <dl>
                  <div><dt>Preis</dt><dd>{formatMoney(state?.price, state?.currency)}</dd></div>
                  <div><dt>Versand</dt><dd>{state?.fulfillmentMode ?? row.fulfillmentMode ?? "–"}</dd></div>
                  <div><dt>Vorlage</dt><dd>{state?.shippingTemplate ?? "–"}</dd></div>
                  <div><dt>Live geprüft</dt><dd>{formatDate(state?.lastVerifiedAt)}</dd></div>
                </dl>
                {state?.issues?.map((issue, index) => (
                  <ListingIssueCard
                    issue={issue}
                    key={`${issue.code ?? "amazon-issue"}-${index}`}
                  />
                ))}
                {status === "suppressed" && !state?.issues?.length && (
                  <div className="listing-issue listing-issue--unknown">
                    <Info size={15} />
                    <div className="listing-issue__body">
                      <strong>Unterdrückung bestätigt · Ursache noch offen</strong>
                      <p>
                        Amazon hat in diesem Abruf keinen konkreten Issue-Code geliefert.
                        Starte die vollständige Diagnose, damit zusätzlich der
                        Suppressed-Listings-Report geprüft wird.
                      </p>
                    </div>
                  </div>
                )}
                {status === "missing" && (
                  <p className="detail-country__empty">In diesem Marketplace existiert keine Seller-SKU.</p>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="listing-detail__safety">
        <ShieldCheck size={18} />
        <div>
          <strong>Bestand ist geschützt</strong>
          <p>Preis-, Bild-, Inhalts- oder Versandänderungen senden niemals eine bestehende Menge.</p>
        </div>
      </section>

      <section>
        <div className="drawer-section-heading"><h3>Content & Medien</h3></div>
        <button className="detail-link-card" onClick={openImageReview}>
          <span><ScanSearch size={18} /></span>
          <div><strong>Alle Produktbilder prüfen</strong><small>Jährliche Sichtprüfung, Bildplätze und Amazon-Live-Abgleich</small></div>
          <ChevronRight size={17} />
        </button>
        <button className="detail-link-card" onClick={openSafetyReview}>
          <span><ShieldCheck size={18} /></span>
          <div><strong>Produktsicherheit prüfen</strong><small>Einmalige Prüfung von Sicherheitsangaben, Gefahrensymbolen und GPSR</small></div>
          <ChevronRight size={17} />
        </button>
        <button className="detail-link-card" onClick={openQuality}>
          <span><CircleAlert size={18} /></span>
          <div>
            <strong>Fehler und Unterdrückung erklären</strong>
            <small>Amazon-Grund, betroffene Felder und nächsten Schritt anzeigen</small>
          </div>
          <ChevronRight size={17} />
        </button>
        <button className="detail-link-card" onClick={openMedia}>
          <span><SlidersHorizontal size={18} /></span>
          <div><strong>Bilder bearbeiten</strong><small>Zuschneiden, drehen, weißer Hintergrund, Reihenfolge</small></div>
          <ChevronRight size={17} />
        </button>
      </section>
    </div>
  );
}

function ListingIssueCard({ issue }: { issue: MarketplaceListingIssue }) {
  const actions = issue.enforcementActions ?? [];
  const reportedActions =
    issue.reportedEnforcementActions ?? issue.rawEnforcementActions ?? [];
  const exempt =
    issue.exemptionActive ??
    ["EXEMPT", "EXEMPT_UNTIL_EXPIRY_DATE"].includes(
      String(issue.exemptionStatus ?? "").toUpperCase(),
    );
  const amazonConfirmed =
    issue.evidenceClass === "AMAZON_CONFIRMED" ||
    String(issue.source ?? "").startsWith("AMAZON_");
  const isError = String(issue.severity ?? "").toUpperCase() === "ERROR";

  return (
    <div className="listing-issue">
      {isError ? <XCircle size={15} /> : <AlertTriangle size={15} />}
      <div className="listing-issue__body">
        <div className="listing-issue__heading">
          <strong>{issue.code ?? "Amazon-Hinweis"}</strong>
          <small>{amazonConfirmed ? "Von Amazon bestätigt" : "Lokaler Hinweis"}</small>
        </div>
        <p>{issue.message}</p>
        {issue.explanation && issue.explanation !== issue.message && (
          <p className="listing-issue__explanation">
            <strong>Auswirkung:</strong> {issue.explanation}
          </p>
        )}
        {Boolean(issue.attributeNames?.length) && (
          <div className="listing-issue__fields">
            <span>Betroffen:</span>
            {issue.attributeNames?.map((field) => <code key={field}>{field}</code>)}
          </div>
        )}
        {actions.length > 0 && (
          <div className="listing-issue__actions">
            {actions.map((action) => (
              <span key={action}>{enforcementLabel(action)}</span>
            ))}
          </div>
        )}
        {exempt && (
          <div className="listing-issue__exemption">
            <ShieldCheck size={13} />
            <span>
              Amazon-Ausnahme aktiv
              {reportedActions.length
                ? ` · ${reportedActions.map(enforcementLabel).join(", ")} wird derzeit nicht angewendet`
                : ""}
              {issue.exemptionExpiry ? ` · bis ${formatDate(issue.exemptionExpiry)}` : ""}
            </span>
          </div>
        )}
        {issue.recommendation && (
          <div className="listing-issue__recommendation">
            <Lightbulb size={13} />
            <span>{issue.recommendation}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function enforcementLabel(action: string): string {
  const labels: Record<string, string> = {
    LISTING_SUPPRESSED: "Angebot nicht kaufbar",
    SEARCH_SUPPRESSED: "Aus Suche ausgeblendet",
    ATTRIBUTE_SUPPRESSED: "Attribut verworfen",
    CATALOG_ITEM_REMOVED: "Katalogartikel entfernt",
  };
  return labels[action] ?? action.replaceAll("_", " ");
}

function RolloutModal({
  open,
  row,
  onClose,
  notify,
}: {
  open: boolean;
  row: ListingMatrixRow;
  onClose: () => void;
  notify: PageContext["notify"];
}) {
  const repricingBlockedCountries = useMemo(
    () =>
      MARKETPLACE_CODES.filter(
        (code) =>
          code !== "DE" && row.marketplaces[code]?.repricing?.status !== "inactive",
      ),
    [row],
  );
  const availableCountries = useMemo(
    () =>
      MARKETPLACE_CODES.filter(
        (code) =>
          code !== "DE" &&
          row.marketplaces[code]?.status === "missing" &&
          row.marketplaces[code]?.repricing?.status === "inactive",
      ),
    [row],
  );
  const [countries, setCountries] = useState<MarketplaceCode[]>(availableCountries);
  const [fulfillment, setFulfillment] = useState<FulfillmentMode>(
    row.fulfillmentMode ?? "FBM",
  );
  const dePrice = row.marketplaces.DE?.price ?? 0;
  const [prices, setPrices] = useState<Partial<Record<MarketplaceCode, number>>>({});
  const [skuIdentity, setSkuIdentity] = useState<Pick<ListingDraft, "gtin" | "billbeeId" | "identityId" | "skuUnit">>({});
  const [shipping, setShipping] = useState<Partial<Record<MarketplaceCode, string>>>({});
  const [shippingOptions, setShippingOptions] = useState<
    Partial<Record<MarketplaceCode, ShippingTemplate[]>>
  >({});
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [preview, setPreview] = useState<ListingPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const previewRequest = useRef(0);
  const invalidatePreview = () => {
    previewRequest.current += 1;
    setPreview(null);
    setPreviewing(false);
  };
  const close = () => { invalidatePreview(); onClose(); };
  useEffect(() => () => { previewRequest.current += 1; }, []);
  const rolloutSettings = useAsync(api.settings, []);
  const preparationRequest = useRef(0);
  const [preparation, setPreparation] = useState<RolloutPreparation | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [marketplaceAttributes, setMarketplaceAttributes] = useState<Partial<Record<MarketplaceCode, Record<string, unknown>>>>({});
  const prepareCountries = async () => {
    const requestId = ++preparationRequest.current;
    setPreparing(true);
    setError(null);
    invalidatePreview();
    try {
      const result = await api.prepareRollout({ sellerSku: row.sellerSku, targetMarketplaces: countries });
      if (requestId !== preparationRequest.current) return;
      setPreparation(result);
      setMarketplaceAttributes((previous) => ({
        ...Object.fromEntries(result.marketplaces.map((item) => [item.code, item.attributes ?? {}])),
        ...previous,
      }));
    } catch (reason) { if (requestId === preparationRequest.current) setError(getApiError(reason)); }
    finally { if (requestId === preparationRequest.current) setPreparing(false); }
  };
  useEffect(() => {
    preparationRequest.current += 1;
    invalidatePreview();
    setSkuIdentity({});
    setPreparing(false);
    setPreparation(null);
    setMarketplaceAttributes({});
  }, [open, row.sellerSku]);
  const shippingSettings = useAsync(api.shippingSettings, []);

  useEffect(() => {
    if (!open) return;
    setCountries(availableCountries);
    setFulfillment("FBM");
    setPrices(
      Object.fromEntries(
        availableCountries.map((code) => [
          code,
          calculateForeignPrice(dePrice, code, rolloutSettings.data),
        ]),
      ),
    );
    setShipping({});
    invalidatePreview();
    setDone(null);
    setError(null);
  }, [open, row, availableCountries.join(","), dePrice, rolloutSettings.data]);

  useEffect(() => {
    if (!open || fulfillment !== "FBM" || countries.length === 0) return;
    let active = true;
    setLoadingShipping(true);
    Promise.all(countries.map(async (code) => [code, await api.shippingTemplates(code)] as const))
      .then((items) => {
        if (!active) return;
        invalidatePreview();
        setShippingOptions(Object.fromEntries(items));
        setShipping((current) => {
          const next = { ...current };
          items.forEach(([code, templates]) => {
            if (next[code]) return;
            const configuredDefault = shippingSettings.data?.defaultTemplates[code];
            if (
              configuredDefault &&
              templates.some(
                (template) =>
                  (template.templateId ?? template.id ?? template.name) === configuredDefault,
              )
            ) {
              next[code] = configuredDefault;
            }
          });
          return next;
        });
      })
      .catch((reason) => {
        if (active) setError(getApiError(reason));
      })
      .finally(() => {
        if (active) setLoadingShipping(false);
      });
    return () => {
      active = false;
    };
  }, [open, fulfillment, countries.join(","), shippingSettings.data]);

  const incompleteCountries = countries.filter(
    (code) =>
      (prices[code] ?? 0) < calculateForeignPrice(dePrice, code, rolloutSettings.data) ||
      (fulfillment === "FBM" && !shipping[code]),
  );
  const canPreview = Boolean(skuIdentity.skuUnit) && countries.length > 0 && incompleteCountries.length === 0 && !preparing &&
    countries.every((code) => preparation?.marketplaces.some((item) => item.code === code && !item.error));

  const createPreview = async () => {
    const requestId = ++previewRequest.current;
    setPreviewing(true);
    setError(null);
    try {
      const result = await api.previewRollout({
        sellerSku: row.sellerSku,
        ...skuIdentity,
        marketplaceAttributes,
        targetMarketplaces: countries,
        fulfillmentMode: fulfillment,
        startPrices: prices,
        shippingTemplates: shipping,
      });
      if (requestId !== previewRequest.current) return;
      setPreview(result);
    } catch (reason) {
      if (requestId === previewRequest.current) setError(getApiError(reason));
    } finally {
      if (requestId === previewRequest.current) setPreviewing(false);
    }
  };

  const submit = async () => {
    if (!preview) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.submitRollout({
        previewId: preview.previewId,
        confirmationToken: preview.confirmationToken,
        idempotencyKey: `rollout-${preview.previewId}`,
      });
      setDone(result.jobId);
      notify(`Rollout für ${row.sellerSku} wurde eingereiht.`, "success");
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Fehlende Auslandsangebote anlegen"
      description={`${row.sellerSku} · Erst Angaben vervollständigen, dann prüfen und bewusst an Amazon senden.`}
      size="large"
      footer={
        done ? (
          <Button onClick={close}>Schließen</Button>
        ) : preview ? (
          <>
            <span className="rollout-footer-status rollout-footer-status--ready">
              <CheckCircle2 size={16} /> {preview.marketplaces.length} Länder geprüft
            </span>
            <Button variant="ghost" onClick={invalidatePreview}>Zurück</Button>
            <Button
              disabled={!preview.canSubmit}
              loading={submitting}
              onClick={submit}
              icon={<Send size={16} />}
            >
              Angebote jetzt an Amazon senden
            </Button>
          </>
        ) : (
          <>
            <span className={`rollout-footer-status ${canPreview ? "rollout-footer-status--ready" : "rollout-footer-status--open"}`}>
              {canPreview ? <CheckCircle2 size={16} /> : <CircleAlert size={16} />}
              {countries.length === 0
                ? "Mindestens ein Zielland auswählen"
                : incompleteCountries.length > 0
                  ? `Noch ${incompleteCountries.length} ${incompleteCountries.length === 1 ? "Land" : "Länder"} vervollständigen`
                  : "Alle Pflichtangaben vollständig"}
            </span>
            <Button variant="ghost" onClick={close}>Abbrechen</Button>
            <Button
              disabled={!canPreview}
              loading={previewing}
              onClick={createPreview}
              icon={<ShieldCheck size={16} />}
            >
              Änderungen prüfen
            </Button>
          </>
        )
      }
    >
      {error && <InlineError message={error} />}
      {!done && (
        <ol className="rollout-steps" aria-label="Ablauf des Länder-Rollouts">
          <li className={!preview ? "is-active" : "is-complete"}>
            <span>{preview ? <Check size={14} /> : "1"}</span>
            <div><strong>Angebote konfigurieren</strong><small>Länder, Preis und Versand festlegen</small></div>
          </li>
          <li className={preview ? "is-active" : ""}>
            <span>2</span>
            <div><strong>Prüfen und senden</strong><small>Änderungen vor Amazon kontrollieren</small></div>
          </li>
        </ol>
      )}
      {done ? (
        <div className="modal-success">
          <span><Check size={28} /></span>
          <StatusPill status="queued" />
          <h3>Rollout wurde eingereiht</h3>
          <p>
            Job <code>{done}</code> übermittelt die bestätigten Angebote und prüft danach
            ihren tatsächlichen Live-Status.
          </p>
        </div>
      ) : preview ? (
        <div className="rollout-preview">
          <div className="rollout-preview__summary">
            <ShieldCheck size={20} />
            <div>
              <strong>Das wird an Amazon übermittelt</strong>
              <p>Kontrolliere jedes Land. Erst der blaue Button unten startet die Übertragung.</p>
            </div>
          </div>
          {preview.marketplaces.map((item) => (
            <article key={item.code}>
              <header>
                <MarketplaceMark code={item.code} decorative />
                <div>
                  <strong>{MARKETPLACE_META[item.code].label}</strong>
                  <small>{item.operation}</small>
                </div>
                <StatusPill
                  status={item.errors.length ? "error" : item.warnings.length ? "warning" : "success"}
                  label={item.errors.length ? "Blockiert" : "Bereit"}
                />
                <RepricingBadge repricing={item.repricing} />
              </header>
              <div className="rollout-preview__diff">
                {item.diff.length ? item.diff.map((diff, index) => (
                  <div key={index}>
                    <code>{diff.path ?? diff.field ?? "Feld"}</code>
                    <span>{formatPreviewValue(diff.before, "nicht gesetzt")}</span>
                    <ChevronRight size={14} />
                    <strong>{formatPreviewValue(diff.after, "entfernt")}</strong>
                  </div>
                )) : <p>Keine Änderung erforderlich.</p>}
              </div>
              {item.errors.map((message) => <p className="preview-message preview-message--error" key={message}>{message}</p>)}
              {item.warnings.map((message) => <p className="preview-message preview-message--warning" key={message}>{message}</p>)}
            </article>
          ))}
          <div className="availability-rule">
            <ShieldCheck size={17} />
            <span>Bestehende Mengen werden bei diesem Rollout grundsätzlich nicht mitgesendet.</span>
          </div>
        </div>
      ) : (
        <div className="rollout-form">
          <section className="quality-steps">
            <h3>Fehlende Länder aus dem DE-Listing vorbereiten</h3>
            <p>Ist die ASIN im Zielland vorhanden, wird das Angebot angelegt. Fehlt der Katalogartikel, übernehmen wir passende Produktdaten aus DE und zeigen die ergänzbaren Pflichtfelder je Land.</p>
            <Button variant="secondary" loading={preparing} disabled={!countries.length}
              onClick={() => void prepareCountries()}>Ausgewählte Länder prüfen · DE-Daten übernehmen</Button>
            {preparation?.marketplaces.filter((item) => countries.includes(item.code)).map((item) => (
              <section key={item.code} className="quality-steps">
                <h3><MarketplaceMark code={item.code} /> {item.catalogAvailable ? "Katalogartikel vorhanden · Angebot bereit" : "Produktdaten für das Zielland"}</h3>
                {item.error ? <InlineError message={item.error} onRetry={() => void prepareCountries()} /> : item.catalogAvailable ?
                  <p>Keine erneute Eingabe der Produktdaten erforderlich.</p> : <>
                    <p>{item.message}</p>
                    {item.sourceTitle && <p>DE-Titel als Referenz: <strong>{item.sourceTitle}</strong></p>}
                    <ProductTypeAttributesForm productType={preparation.productType}
                      definition={item.definition ?? null} loading={false} error={null}
                      attributes={marketplaceAttributes[item.code] ?? {}}
                      onChange={(attributes) => { setMarketplaceAttributes((current) => ({ ...current, [item.code]: attributes })); invalidatePreview(); }}
                      onRetry={() => void prepareCountries()} />
                  </>}
              </section>
            ))}
          </section>
          <div className="rollout-intro">
            <span><ArrowUpRight size={20} /></span>
            <div>
              <strong>Neue Auslandsangebote anlegen</strong>
              <p>Nur ausgewählte Länder werden vorbereitet. Bestehende Angebote und Mengen bleiben unverändert.</p>
            </div>
            <strong>{countries.length} ausgewählt</strong>
          </div>
          <Field
            label="1. Zielländer auswählen"
            hint="Blau markierte Länder werden angelegt. Deutschland ist die Preisbasis und bleibt unverändert."
          >
            <MarketplaceSelector
              value={countries}
              multiple
              label=""
              disabledCodes={MARKETPLACE_CODES.filter(
                (code) => !availableCountries.includes(code),
              )}
              onChange={(value) => {
                setCountries(value as MarketplaceCode[]);
                invalidatePreview();
              }}
            />
          </Field>
          <div
            className={
              "repricing-policy " +
              (repricingBlockedCountries.length
                ? "repricing-policy--blocked"
                : "repricing-policy--open")
            }
          >
            {repricingBlockedCountries.length ? <ShieldCheck size={18} /> : <Check size={18} />}
            <div>
              <strong>
                {repricingBlockedCountries.length
                  ? "Preisänderungen für diese Länder gesperrt"
                  : "Preisänderungen für die auswählbaren Länder freigegeben"}
              </strong>
              <p>
                {repricingBlockedCountries.length
                  ? repricingBlockedCountries
                      .map((code) => MARKETPLACE_META[code].label)
                      .join(", ") +
                    " werden nicht automatisch verändert, weil der Amazon-Repricer dort aktiv oder sein Status unbekannt ist."
                  : "Der Amazon-Repricer ist für die auswählbaren Länder nicht aktiv. Der Startpreis darf beim Anlegen übermittelt werden."}
              </p>
            </div>
          </div>
          <SkuIdentityFields gtin={skuIdentity.gtin} billbeeId={skuIdentity.billbeeId} identityId={skuIdentity.identityId} unit={skuIdentity.skuUnit}
            onChange={value => { setSkuIdentity(value); invalidatePreview(); }} />
          <Field label="2. Versandart festlegen">
            <div className="segmented">
              <button
                className={fulfillment === "FBM" ? "is-active" : ""}
                onClick={() => { invalidatePreview(); setFulfillment("FBM"); }}
                type="button"
              >
                <Truck size={18} />
                <span><strong>FBM</strong><small>Versand durch Playzeug</small></span>
              </button>
              <button
                className={fulfillment === "FBA" ? "is-active" : ""}
                onClick={() => undefined}
                disabled
                type="button"
                title="FBA in Version 1 nur DE"
              >
                <Store size={18} />
                <span><strong>FBA</strong><small>Für Ausland noch nicht verfügbar</small></span>
              </button>
            </div>
            <span className="field__hint">
              Die fehlenden Auslandsangebote werden als FBM angelegt. FBA bleibt nur für Deutschland aktiv.
            </span>
          </Field>
          <div className="rollout-section-heading">
            <div><strong>3. Angaben je Land vervollständigen</strong><small>Preis und Versandvorlage sind Pflichtangaben.</small></div>
            <span className={incompleteCountries.length ? "needs-attention" : "is-ready"}>
              {incompleteCountries.length ? `${incompleteCountries.length} offen` : "Alles bereit"}
            </span>
          </div>
          <div className="rollout-country-grid">
            {countries.map((code) => {
              const minimumPrice = calculateForeignPrice(dePrice, code, rolloutSettings.data);
              const priceReady = (prices[code] ?? 0) >= minimumPrice;
              const shippingReady = fulfillment === "FBA" || Boolean(shipping[code]);
              const countryReady = priceReady && shippingReady;
              return (
              <article key={code} className={countryReady ? "is-ready" : "needs-attention"}>
                <header>
                  <MarketplaceMark code={code} decorative />
                  <div><strong>{MARKETPLACE_META[code].label}</strong><small>{MARKETPLACE_META[code].vat}% MwSt.</small></div>
                  <span className={`rollout-readiness ${countryReady ? "is-ready" : "needs-attention"}`}>
                    {countryReady ? <CheckCircle2 size={14} /> : <CircleAlert size={14} />}
                    {countryReady ? "Bereit" : "Angaben fehlen"}
                  </span>
                </header>
                <Field
                  label="Startpreis"
                  hint={`DE-Preis ${formatMoney(dePrice)} · erlaubtes Minimum ${formatMoney(minimumPrice)}`}
                  error={
                    !priceReady
                      ? "Auslandspreis-Schutzregel noch nicht erfüllt."
                      : undefined
                  }
                >
                  <div className="money-input">
                    <Input
                      type="number"
                      step="0.01"
                      min={dePrice + 0.01}
                      value={prices[code] ?? ""}
                      onChange={(event) => {
                        invalidatePreview();
                        setPrices((current) => ({
                          ...current,
                          [code]: Number(event.target.value),
                        }));
                      }}
                    />
                    <span>EUR</span>
                  </div>
                </Field>
                {fulfillment === "FBM" && (
                  <Field
                    label="Amazon-Versandvorlage"
                    error={!loadingShipping && !shippingReady ? "Pflichtfeld: Versandvorlage auswählen." : undefined}
                  >
                    <Select
                      className={!loadingShipping && !shippingReady ? "is-required" : ""}
                      value={shipping[code] ?? ""}
                      disabled={loadingShipping}
                      onChange={(event) => {
                        invalidatePreview();
                        setShipping((current) => ({ ...current, [code]: event.target.value }));
                      }}
                    >
                      <option value="">{loadingShipping ? "Lädt …" : "Bitte wählen …"}</option>
                      {(shippingOptions[code] ?? []).map((template) => (
                        <option
                          key={template.templateId ?? template.id ?? template.name}
                          value={template.templateId ?? template.id ?? template.name}
                        >
                          {template.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
              </article>
              );
            })}
            {countries.length === 0 && (
              <div className="rollout-empty-selection">
                <CircleAlert size={22} />
                <strong>Kein Zielland ausgewählt</strong>
                <p>Wähle oben mindestens ein blaues Länderfeld aus.</p>
              </div>
            )}
          </div>
          <div className="price-policy">
            <Info size={18} />
            <div>
              <strong>Wichtig: Der Preis wird genau einmal gesetzt</strong>
              <p>
                Danach übernimmt wieder der Repricer. Der Amazon Manager verändert keine
                Min-/Max-Preise und sendet bei diesem Vorgang keinen Bestand.
              </p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function roundTo95(value: number) {
  const whole = Math.floor(value);
  const candidate = whole + 0.95;
  return Number((candidate >= value ? candidate : whole + 1.95).toFixed(2));
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function formatPreviewValue(value: unknown, emptyLabel: string): string {
  if (value === null || value === undefined || value === "") return emptyLabel;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    const formatted = value
      .map((item) => formatPreviewValue(item, ""))
      .filter(Boolean);
    return formatted.length ? formatted.join(" · ") : emptyLabel;
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (record.value_with_tax !== undefined) {
      return `${formatPreviewValue(record.value_with_tax, "–")} EUR`;
    }
    if (record.value !== undefined) {
      return formatPreviewValue(record.value, emptyLabel);
    }
    if (record.fulfillment_channel_code !== undefined) {
      const channel = formatPreviewValue(record.fulfillment_channel_code, "–");
      return record.quantity === undefined
        ? channel
        : `${channel} · Menge ${formatPreviewValue(record.quantity, "0")}`;
    }
    const entries = Object.entries(record)
      .filter(([key]) => !["marketplace_id", "marketplaceId"].includes(key))
      .map(([key, item]) => `${previewFieldLabel(key)}: ${formatPreviewValue(item, "–")}`);
    return entries.length ? entries.join(" · ") : emptyLabel;
  }
  return String(value);
}

function previewFieldLabel(key: string): string {
  const labels: Record<string, string> = {
    audience: "Zielgruppe",
    currency: "Währung",
    our_price: "Preis",
    schedule: "Startpreis",
  };
  return labels[key] ?? key;
}

function calculateForeignPrice(
  dePrice: number,
  code: MarketplaceCode,
  settings: AppSettings | null,
) {
  const net = dePrice / 1.19;
  const targetVat = MARKETPLACE_META[code].vat / 100;
  const buffer = settings?.marketplaceBuffers?.[code];
  const shipping = buffer?.shippingEur ?? 0;
  const fee = (buffer?.feePercent ?? 0) / 100;
  const vatAdjusted = (net + shipping) * (1 + targetVat);
  const feeAdjusted = fee >= 1 ? Number.POSITIVE_INFINITY : vatAdjusted / (1 - fee);
  const percent = (settings?.priceGuard?.minimumPercent ?? 5) / 100;
  const absolute = settings?.priceGuard?.minimumAbsoluteEur ?? 2.5;
  const guarded = Math.max(
    feeAdjusted,
    dePrice * (1 + percent),
    dePrice + absolute,
  );
  return roundTo95(guarded);
}
